import { useState, useEffect } from "react"
import { Box, Icon, Link, Text } from "@chakra-ui/react";
import { FiExternalLink, FiPlayCircle } from "react-icons/fi"
import { isWithinInterval } from "date-fns"

import specialCases from "config/special"
import channels from "services/craft/queries/channels";

// The live-channel / on-demand / special-case link for an event, or a null href when there is none.
// Shared by RenderLink and the program guide's SessionDetail, which places the link itself.
export function useEventLink(event, channelSlug, site) {
    const [href, setHref] = useState(null)
    const [linkIcon, setLinkIcon] = useState("play")

    useEffect(() => {
        function getChannel(channelSlug, sessionTypeId) {
            if (channelSlug) {
                return site.channels?.find(channel => channel.slug === channelSlug)
            }
            return site.channels?.find(channel => channel.sessionTypeID == sessionTypeId)
        }

        // The channel found by session type when no slug is given carries its own slug; build the
        // URL from that rather than from a missing `channelSlug` (/live/null/).
        function setLiveLink(channel) {
            // Link to the live channel page if event date is today & it's not on-demand only
            const endTime = event.datetime_end_buffer ? event.datetime_end_buffer : new Date(event.datetime_end)
            if (
                event.depth === 1 &&
                !event.params.on_demand &&
                // TODO: would be nice to use the mock time here rather than now (new Date()) -- requires moving the mockClock to a provider
                isWithinInterval(new Date(), {
                    start: new Date(event.datetime_start),
                    end: endTime,
                })
            ) {
                setHref("/live/" + (channelSlug || channel.slug) + "/")
            } else {
                setHref(null)
            }
        }

        const videoLink = event.links?.find(link => link.type === "video" && link.id)

        // Link to an external URL if we have an override for it
        if (specialCases[site.meeting.emmaMeetingId]?.alternateLinks?.hasOwnProperty(event.id)) {
            setHref(specialCases[site.meeting.emmaMeetingId].alternateLinks[event.id])
            setLinkIcon("external")
        } else if (videoLink) {
            const channel = getChannel(channelSlug, event.session_type_id)

            if (!channel || !channel.currentlyAvailableVersions.includes("ondemand") || parseInt(channel.videoLinksLevel, 10) -1 !== event.depth) return

            if (channel.altVideoLinksPage?.length) {
                setHref("/" + channel.altVideoLinksPage[0].slug + "/" + videoLink.id)
            } else {
                setHref("/ondemand/" + (channelSlug || channel.slug) + "/" + event.id)
            }
        } else if (site.meeting.isLive) {
            const channel = getChannel(channelSlug, event.session_type_id)
            if (
                !channel ||
                channel.channelStatus === "In-person only" ||
                !channel.currentlyAvailableVersions.includes("live")
            )
                return

            // update the links every 30 seconds during the meeting
            const refresh = setInterval(() => {
                setLiveLink(channel)
            }, 30000)

            setLiveLink(channel)

            return () => clearInterval(refresh)
        }
    }, [channelSlug, event, site.meeting, site.channels])

    return { href, linkIcon }
}

export default function RenderLink({ event, channelSlug, site, children }) {
    const { href, linkIcon } = useEventLink(event, channelSlug, site)

    if (!href) return <Text as="span">{children}</Text>

    return (
        <Link href={href} textDecoration="underline" target="_blank" align="center" position="relative">
            {children}
            <Box display="inline" pl={1} verticalAlign="middle" position="absolute">
                <Icon as={linkIcon === "play" ? FiPlayCircle : FiExternalLink} w="20px" h="20px" />
            </Box>
        </Link>
    )
}
